/**
 * Your own lists of friends.
 *
 * "Ranked squad", "the weekend lot" - the way people actually think about who
 * they play with, which is never one flat list of everybody. Private: nobody
 * is told they are in one, there is nothing to accept, and it is nothing like
 * a crew. It is a view of your friends, for you.
 *
 * Two things it does: filters the friends page, and fills an invite in one
 * press when the whole list is who you meant.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/lib/api';
import type { PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { UserName } from './user';
import { Button, Modal, useToast } from './ui';

type FriendList = { name: string; members: PublicUser[] };

export function useFriendLists() {
  return useQuery({
    queryKey: ['friend-lists'],
    queryFn: () => api.get<{ lists: FriendList[]; max: number }>('/api/friends/lists'),
    staleTime: 60_000
  });
}

/** The row of list chips above the friends list. */
export function FriendListBar({ active, onPick }: { active: string; onPick: (name: string) => void }) {
  const [editing, setEditing] = useState<string | null>(null);
  const [making, setMaking] = useState(false);
  const lists = useFriendLists();
  const rows = lists.data?.lists ?? [];

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip on={!active} onClick={() => onPick('')}>Everyone</Chip>
        {rows.map((l) => (
          <span key={l.name} className="flex items-center">
            <Chip on={active === l.name} onClick={() => onPick(l.name)}>
              {l.name}
              <span className={cn('ml-1.5 tabular-nums', active === l.name ? 'opacity-75' : 'text-ghost')}>{l.members.length}</span>
            </Chip>
            <button
              type="button"
              aria-label={'Edit ' + l.name}
              onClick={() => setEditing(l.name)}
              className="ml-0.5 grid h-7 w-7 place-items-center rounded-full text-ghost outline-none transition-colors hover:bg-surface-2 hover:text-ink"
            >
              <Icon name="edit" size={12} />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => setMaking(true)}
          className="flex h-8 items-center gap-1.5 rounded-full px-3 text-[12.5px] font-semibold text-mute outline-none transition-colors hover:text-ink"
        >
          <Icon name="plus" size={13} />
          New list
        </button>
      </div>

      {making || editing ? (
        <EditList
          name={editing ?? ''}
          onClose={() => { setMaking(false); setEditing(null); }}
          onSaved={(name) => onPick(name)}
        />
      ) : null}
    </>
  );
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        'flex h-8 items-center rounded-full px-3.5 text-[12.5px] font-semibold outline-none transition-colors focus-visible:shadow-[0_0_0_2px_var(--accent)]',
        on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
      )}
    >
      {children}
    </button>
  );
}

function EditList({ name, onClose, onSaved }: { name: string; onClose: () => void; onSaved: (name: string) => void }) {
  const lists = useFriendLists();
  const friends = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends });
  const queryClient = useQueryClient();
  const toast = useToast();

  const existing = (lists.data?.lists ?? []).find((l) => l.name === name);
  const [title, setTitle] = useState(name);
  const [picked, setPicked] = useState<string[]>(() => (existing?.members ?? []).map((u) => u.id));
  const [q, setQ] = useState('');

  const save = useMutation({
    mutationFn: () => api.put<{ name: string }>('/api/friends/lists/' + encodeURIComponent(title.trim()), { members: picked }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['friend-lists'] });
      toast('List saved', 'good');
      onSaved(title.trim());
      onClose();
    },
    onError: (err: Error) => toast(err.message, 'bad')
  });

  const drop = useMutation({
    mutationFn: () => api.del('/api/friends/lists/' + encodeURIComponent(name)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['friend-lists'] });
      toast('List deleted', 'good');
      onSaved('');
      onClose();
    },
    onError: (err: Error) => toast(err.message, 'bad')
  });

  const term = q.trim().toLowerCase();
  const people = (friends.data?.friends ?? [])
    .map((f) => f.user)
    .filter((u) => !term || (u.username + ' ' + u.displayName).toLowerCase().includes(term));

  return (
    <Modal
      title={name ? 'Edit “' + name + '”' : 'New list'}
      subtitle="Private to you. Nobody is told they are in it."
      onClose={onClose}
      footer={
        <>
          {name ? <Button kind="plain" className="mr-auto" onClick={() => drop.mutate()}>Delete list</Button> : null}
          <Button kind="outline" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={!title.trim() || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value.slice(0, 24))}
          placeholder="Ranked squad"
          className="h-[var(--h-md)] w-full rounded-[calc(12px*var(--radius-mul,1))] border border-line bg-surface-1 px-3 text-[14px] outline-none transition-colors placeholder:text-ghost focus:border-accent"
        />

        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search your friends…"
          className="h-10 w-full rounded-[calc(12px*var(--radius-mul,1))] bg-surface-1 px-3 text-[13.5px] shadow-[inset_0_0_0_1px_var(--line)] outline-none placeholder:text-ghost focus:shadow-[inset_0_0_0_1px_var(--accent)]"
        />

        <ul className="m-0 flex max-h-[320px] list-none flex-col gap-1 overflow-y-auto p-0">
          {people.map((u) => {
            const on = picked.includes(u.id);
            return (
              <li key={u.id}>
                <button
                  type="button"
                  aria-pressed={on}
                  onClick={() => setPicked((list) => on ? list.filter((x) => x !== u.id) : [...list, u.id])}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-[calc(12px*var(--radius-mul,1))] p-2 text-left outline-none transition-colors',
                    on ? 'bg-accent-soft' : 'hover:bg-surface-2'
                  )}
                >
                  <Avatar user={u} size={32} />
                  <UserName user={u} className="min-w-0 flex-1 truncate text-[13.5px] font-semibold" />
                  <span className={cn('grid h-5 w-5 flex-none place-items-center rounded-full', on ? 'bg-accent text-accent-fg' : 'shadow-[inset_0_0_0_1.5px_var(--line)]')}>
                    {on ? <Icon name="check" size={12} /> : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        <p className="m-0 text-[12px] text-mute">{picked.length} chosen</p>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------- introductions */

/** "You two should play together." */
export function Introduce({ user, onClose }: { user: PublicUser; onClose: () => void }) {
  const friends = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends });
  const queryClient = useQueryClient();
  const toast = useToast();
  const [other, setOther] = useState('');
  const [q, setQ] = useState('');

  const go = useMutation({
    mutationFn: () => api.post<{ group: { id: string } }>('/api/friends/introduce', { a: user.id, b: other }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast('Introduced. The three of you have a conversation now.', 'good');
      onClose();
    },
    onError: (err: Error) => toast(err.message, 'bad')
  });

  const term = q.trim().toLowerCase();
  const people = (friends.data?.friends ?? [])
    .map((f) => f.user)
    .filter((u) => u.id !== user.id)
    .filter((u) => !term || (u.username + ' ' + u.displayName).toLowerCase().includes(term));

  return (
    <Modal
      title={'Introduce ' + user.displayName}
      subtitle="Both of them are your friends; neither has met the other. You will be in the conversation too."
      onClose={onClose}
      footer={
        <>
          <Button kind="outline" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={!other || go.isPending} onClick={() => go.mutate()}>
            {go.isPending ? 'Introducing…' : 'Introduce them'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Which friend?"
          className="h-10 w-full rounded-[calc(12px*var(--radius-mul,1))] bg-surface-1 px-3 text-[13.5px] shadow-[inset_0_0_0_1px_var(--line)] outline-none placeholder:text-ghost focus:shadow-[inset_0_0_0_1px_var(--accent)]"
        />
        <ul className="m-0 flex max-h-[320px] list-none flex-col gap-1 overflow-y-auto p-0">
          {people.map((u) => {
            const on = other === u.id;
            return (
              <li key={u.id}>
                <button
                  type="button"
                  aria-pressed={on}
                  onClick={() => setOther(u.id)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-[calc(12px*var(--radius-mul,1))] p-2 text-left outline-none transition-colors',
                    on ? 'bg-accent-soft' : 'hover:bg-surface-2'
                  )}
                >
                  <Avatar user={u} size={32} presence />
                  <UserName user={u} className="min-w-0 flex-1 truncate text-[13.5px] font-semibold" />
                  {on ? <Icon name="check" size={14} className="flex-none text-accent" /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </Modal>
  );
}
